/**
 * DividerBlock Component
 * 
 * Visual separator between menu sections.
 * Supports different styles: solid, dashed, dotted, or none.
 */

import React, { memo } from 'react';
import { cn } from '@/lib/utils';
import { DividerBlockProps } from '@/types/menuBuilder';

interface DividerBlockComponentProps {
  props: DividerBlockProps;
  isPreview?: boolean;
}

export const DividerBlock = memo(function DividerBlock({
  props,
}: DividerBlockComponentProps) {
  const { 
    style, 
    thickness = 1, 
    color, 
    spacing = 'md',
    width = 'full',
  } = props;

  const spacingClasses = {
    none: 'py-0',
    sm: 'py-2',
    md: 'py-4',
    lg: 'py-6',
  };

  const widthClasses = {
    full: 'w-full',
    '3/4': 'w-3/4',
    '1/2': 'w-1/2',
    '1/4': 'w-1/4',
  };

  const borderStyleMap: Record<string, React.CSSProperties['borderTopStyle']> = {
    solid: 'solid',
    dashed: 'dashed',
    dotted: 'dotted',
    none: 'none',
  };

  // No divider style
  if (style === 'none') {
    return <div className={spacingClasses[spacing]} />;
  }

  return (
    <div className={cn('flex justify-center', spacingClasses[spacing])}>
      <hr
        className={cn(
          'border-0',
          widthClasses[width]
        )}
        style={{ 
          borderTopWidth: `${thickness}px`,
          borderTopStyle: borderStyleMap[style],
          borderTopColor: color || 'currentColor',
          opacity: color ? 1 : 0.2,
        }}
      />
    </div>
  );
});

export default DividerBlock;
